import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { SnackbarService } from "../../core/services/snackbar.service";
import { AuthService } from "src/app/core/services/auth.service";
import { PanService } from "src/app/core/services/pan.service";
import {
  PanAlreadySearchedDialogComponent,
  PanAlreadySearchedData,
  PanDialogAction,
} from "../../shared/pan-already-searched-dialog/pan-already-searched-dialog.component";
import { PanVerifyResultComponent, PanVerifyResultDialogData } from "../pan-verify-result/pan-verify-result.component";
import { UserService } from "src/app/core/services/user-service.service";

export interface ThemeCard {
  id: number;
  name: string;
  datasetCount: number;
  icon: string;
}

export interface ResultParams {
  pan: string;
  latestRecord: boolean;
}

@Component({
  selector: "app-landing-page",
  templateUrl: "./landing-page.component.html",
  styleUrls: ["./landing-page.component.scss"],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  currentSearchTerm: string = "";

  errorMessage: string = "";
  showErrorMessage: boolean = false;

  panRegex: RegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  panResponse: any = null;
  private subscriptions: Subscription[] = [];

  isLoggedIn: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private router: Router,
    private authService: AuthService,
    private panService: PanService,
    private dialog: MatDialog,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    // Check for error query parameter and read search term
    this.route.queryParams.subscribe((params) => {
      if (params["error"]) {
        this.snackbarService.showError(params["error"]);
      }
      if (params["search"]) {
        this.currentSearchTerm = params["search"];
      }
    });

    const subscription = this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;
    });
    this.subscriptions.push(subscription);
  }

  navigateToLogin(): void {
    this.router.navigate(["/auth/login"]);
  }

  validateSearchTerm(searchTerm: string) {
    if (!this.panRegex.test(searchTerm)) {
      this.errorMessage = "Please enter a valid PAN number";
      this.showErrorMessage = true;
    } else {
      this.errorMessage = "";
      this.showErrorMessage = false;
    }
  }

  onSearchChange(searchTerm: string): void {
    this.currentSearchTerm = searchTerm;

    this.validateSearchTerm(searchTerm);
    if (this.showErrorMessage) {
      return;
    }
    this.fetchPanData(searchTerm);
  }

  fetchPanData(pan: string): void {
    this.panService.verifyPan(pan).subscribe({
      next: (response: any) => {
        const apiResponse = response?.data ?? {};
        this.panResponse = apiResponse;
        const cachedData = apiResponse?.previousSearch ?? null;
        if (apiResponse.fromCache) {
          const modalData: PanAlreadySearchedData = cachedData;
          if (modalData?.searchId || modalData?.transactionId) {
            this.openPanAlreadySearchedDialog(modalData);
          }
        } else {
          const resultParams = { pan, latestRecord: true };
          this.openPanVerifyResultDialog(resultParams);
        }
      },
      error: (error: any) => {
        this.snackbarService.showError(error.error.message ?? 'Something went wrong. Please try again.');
      }
    });
  }

  openPanAlreadySearchedDialog(data: PanAlreadySearchedData): void {
    const dialogRef = this.dialog.open(PanAlreadySearchedDialogComponent, {
      width: "500px",
      data: data,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: PanDialogAction) => {
      if (result === "view_previous") {
        this.router.navigate(['/ngo-diligence', data.transactionId]);
      } else if (result === "fresh_search") {
        const resultParams = {
          pan: this.currentSearchTerm,
          latestRecord: true,
        };
        this.openPanVerifyResultDialog(resultParams);
      } else if (result === "cancel") {
        this.resetSearch();
      }
    });
  }

  openPanVerifyResultDialog(params: ResultParams): void {
    const dialogData: PanVerifyResultDialogData = {
      pan: params.pan,
      latestRecord: params.latestRecord
    };

    const dialogRef = this.dialog.open(PanVerifyResultComponent, {
      width: "600px",
      maxWidth: "90vw",
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === 'cancel') {
        this.resetSearch();
      }
    });
  }

  onSearchFocus(event: boolean): void {
    if (event) {
      if (!this.isLoggedIn) this.navigateToLogin();
      else {
        const user = this.userService.getUserDetails();

        if (!user.isProfileCompleted) {
          const searchInput = document.getElementsByClassName('search-field')[0] as HTMLInputElement;
          if (searchInput) {
            searchInput.blur();
          }
          this.router.navigate(['/user/profile']);

        }
      }
    }
  }

  scrollToSearchContainer() {
    const targetElement = document.getElementById('search-container');
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }

  resetSearch(): void {
    this.currentSearchTerm = "";
    this.errorMessage = "";
    this.showErrorMessage = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}
